import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import useListPacksQuery from 'apollo/resolvers/packs/list';
import useListEntranceTicketTypesQuery from 'apollo/resolvers/entranceTicketTypes/list';
import useListPackAvailabilityQuery from 'apollo/resolvers/packs/listAvailability';
import useListEntranceAvailabilityQuery from 'apollo/resolvers/entranceTicketTypes/listAvailability';
import useGetConfigurationParameters from 'apollo/resolvers/configurationParameters/get';
import {
  DayActivityTimeAvailability,
  EntranceImage,
  PackImage,
  ParkAvailability,
  TicketFeature,
} from 'apollo/types';
import { useParkScheduleQuery } from 'apollo/resolvers/parkAvailability/list';
import { PackOrEntrancePurchase } from 'storage/types';
import { saveItemToPurchase } from 'utils/services/ticketsService';
import getTicketDisplayName from 'utils/helpers/getTicketDisplayName';
import captureEvent from 'utils/post-hog';
import * as events from 'utils/post-hog/events';
import {
  removePurchaseItem,
  startPurchaseOnDate,
  usePurchase,
} from 'storage/purchaseStore';

export type PurchaseItemType = 'packs' | 'entrances';

export interface TimedActivity {
  id: string;
  name: string;
  hasSingleTimetable: boolean;
  activityTimes: DayActivityTimeAvailability[];
}

const dayKey = (isoDate: string): string => isoDate.slice(0, 10);

export interface TicketInformationViewModel {
  isPack: boolean;
  loading: boolean;
  name: string;
  description: string;
  images: (PackImage | EntranceImage)[];
  features: TicketFeature[];
  summerSchedule: ParkAvailability | undefined;
  winterSchedule: ParkAvailability | undefined;
  adultTickets: number;
  otherTickets: number;
  setAdultTickets: (count: number) => void;
  setOtherTickets: (count: number) => void;
  ticketCount: number;
  adultValue: number;
  otherValue: number;
  remaining: number | undefined;
  canAddMoreTickets: boolean;
  selectedDate: string | undefined;
  onSelectDate: (date: string) => void;
  isDateLocked: boolean;
  unavailableDates: string[];
  soldOutDates: string[];
  onMonthChange: (startDate: string, endDate: string) => void;
  timedActivities: TimedActivity[];
  selectedTimes: Record<string, string>;
  onSelectTime: (activityId: string, activityTimeId: string) => void;
  total: number;
  canContinue: boolean;
  isRemoval: boolean;
  onContinue: () => void;
  goBack: () => void;
}

const useTicketInformationViewModel = (): TicketInformationViewModel => {
  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const { item, id } = useParams<{ item: PurchaseItemType; id: string }>();

  const isPack = item === 'packs';

  const storedPurchase = usePurchase();
  const existingItem: PackOrEntrancePurchase | undefined = storedPurchase?.[item].find(
    (entry) => entry?.id === id
  );
  const otherList = isPack ? storedPurchase?.entrances : storedPurchase?.packs;
  const cartHasOtherItems =
    (otherList?.length || 0) > 0 ||
    (storedPurchase?.[item] || []).some((entry) => entry?.id !== id);
  // The cart's date is restored whenever it is relevant, but only *locked* while other
  // items depend on it — editing the only item in the cart can still move it.
  const cartDate = existingItem || cartHasOtherItems ? storedPurchase?.date : undefined;
  const lockedDate = cartHasOtherItems ? storedPurchase?.date : undefined;

  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    cartDate ?? new URLSearchParams(search).get('date') ?? undefined
  );

  const [range, setRange] = useState<{ startDate: string; endDate: string }>();

  const onMonthChange = useCallback((startDate: string, endDate: string) => {
    setRange((current) =>
      current?.startDate === startDate && current?.endDate === endDate
        ? current
        : { startDate, endDate }
    );
  }, []);

  const { data: packsData, loading: loadingPacks } = useListPacksQuery();
  const { data: entrancesData, loading: loadingEntrances } =
    useListEntranceTicketTypesQuery();
  const { data: configurationData } = useGetConfigurationParameters();
  const {
    summerSchedule,
    winterSchedule,
    loading: loadingParkSchedule,
  } = useParkScheduleQuery();

  const staticPack = isPack
    ? packsData?.listPacks.find((pack) => pack.id === id)
    : undefined;
  const staticEntrance = isPack
    ? undefined
    : entrancesData?.listEntranceTicketTypes.find((entrance) => entrance.id === id);

  const packDays = useListPackAvailabilityQuery(
    id,
    range?.startDate || '',
    range?.endDate || '',
    !isPack || !range
  );
  const entranceDays = useListEntranceAvailabilityQuery(
    id,
    range?.startDate || '',
    range?.endDate || '',
    isPack || !range
  );

  const availabilityDays = useMemo(
    () => (isPack ? packDays : entranceDays) || [],
    [isPack, packDays, entranceDays]
  );

  const selectedDayAvailability = useMemo(
    () =>
      selectedDate
        ? availabilityDays.find((day) => dayKey(day.day) === dayKey(selectedDate))
        : undefined,
    [availabilityDays, selectedDate]
  );

  const [adultTickets, setAdultTickets] = useState(existingItem?.adultTicketCount || 0);
  const [otherTickets, setOtherTickets] = useState(existingItem?.otherTicketCount || 0);
  const [selectedTimes, setSelectedTimes] = useState<Record<string, string>>(() =>
    (existingItem?.activities || [])
      .filter((activity) => activity.activityTimeId)
      .reduce(
        (times, activity) => ({ ...times, [activity.id]: activity.activityTimeId }),
        {}
      )
  );

  const ticketCount = adultTickets + otherTickets;

  const adultValue = staticPack?.adultValue ?? staticEntrance?.adultValue ?? 0;
  const otherValue = staticPack?.otherValue ?? staticEntrance?.otherValue ?? 0;
  const remaining = selectedDayAvailability?.remaining;

  const oversold = configurationData?.getConfigurationParameters.oversold?.value || 0;

  const canAddMoreTickets =
    remaining === undefined || ticketCount < remaining + (remaining ? oversold : 0);

  const soldOutDates = useMemo(
    () => availabilityDays.filter((day) => day.remaining <= 0).map((day) => day.day),
    [availabilityDays]
  );

  const unavailableDates = useMemo(
    () =>
      availabilityDays
        .filter((day) => day.remaining > 0 && day.remaining < ticketCount)
        .map((day) => day.day),
    [availabilityDays, ticketCount]
  );

  const groupedActivities = useMemo<TimedActivity[]>(() => {
    const names = new Map(
      (staticPack?.activities || []).map((activity) => [activity.id, activity.name])
    );
    const byActivity = new Map<string, TimedActivity>();
    (selectedDayAvailability?.activityTimes || []).forEach((activityTime) => {
      const current = byActivity.get(activityTime.activityId);
      if (current) {
        current.activityTimes.push(activityTime);
        return;
      }
      byActivity.set(activityTime.activityId, {
        id: activityTime.activityId,
        name: names.get(activityTime.activityId) || '',
        hasSingleTimetable: activityTime.hasSingleTimetable,
        activityTimes: [activityTime],
      });
    });
    return Array.from(byActivity.values());
  }, [selectedDayAvailability, staticPack]);

  const timedActivities = useMemo(
    () => groupedActivities.filter((activity) => !activity.hasSingleTimetable),
    [groupedActivities]
  );

  // A new date invalidates every previously chosen slot, but the first run must not
  // clear the slots restored from the cart.
  const timesDate = useRef(selectedDate);
  useEffect(() => {
    if (timesDate.current === selectedDate) return;
    timesDate.current = selectedDate;
    setSelectedTimes({});
  }, [selectedDate]);

  const applyDate = (date: string) => {
    setSelectedDate(date);
    history.replace(`${history.location.pathname}?date=${date}`);
  };

  const onSelectDate = (date: string) => {
    if (date === selectedDate) return;
    if (!lockedDate) {
      applyDate(date);
      return;
    }
    Modal.confirm({
      title: t('pages.ticketInformation.changeDateTitle'),
      content: t('pages.ticketInformation.changeDateWarning'),
      okText: t('pages.ticketInformation.changeDateConfirm'),
      cancelText: t('pages.ticketInformation.changeDateCancel'),
      onOk: () => {
        startPurchaseOnDate(date);
        applyDate(date);
      },
    });
  };

  const onSelectTime = (activityId: string, activityTimeId: string) => {
    setSelectedTimes((current) => ({ ...current, [activityId]: activityTimeId }));
  };

  const purchaseActivities = groupedActivities.map((activity) => {
    const activityTimeId = activity.hasSingleTimetable
      ? activity.activityTimes[0]?.activityTimeId || ''
      : selectedTimes[activity.id] || '';
    const chosenTime = activity.activityTimes.find(
      (activityTime) => activityTime.activityTimeId === activityTimeId
    );
    return {
      id: activity.id,
      ticketCount,
      activityTimeId,
      name: activity.name,
      startHour: chosenTime?.startHour,
      endHour: chosenTime?.endHour,
    };
  });

  const hasEveryRequiredTime = purchaseActivities.every(
    (activity) => activity.activityTimeId !== ''
  );

  // Dropping an item already in the cart to zero tickets removes it.
  const isRemoval = Boolean(existingItem) && ticketCount === 0;

  const canContinue =
    isRemoval ||
    Boolean(ticketCount > 0 && selectedDate && (!isPack || hasEveryRequiredTime));

  const onContinue = () => {
    if (!canContinue) return;
    if (isRemoval) {
      removePurchaseItem(item, id);
      history.push('/ticketSelection');
      return;
    }
    if (!selectedDate) return;
    const purchaseItem: PackOrEntrancePurchase = {
      id,
      adultTicketCount: adultTickets,
      otherTicketCount: otherTickets,
      activities: isPack ? purchaseActivities : [],
      name: staticPack?.name || staticEntrance?.name || '',
      adultValue,
      otherValue,
    };
    saveItemToPurchase(selectedDate, item, purchaseItem);
    captureEvent(events.toPurchaseSummaryButtonClicked, {
      flow: events.flows.purchase,
      item_type: isPack ? 'pack' : 'entrance',
      item_id: id,
      item_name: getTicketDisplayName(purchaseItem.name),
      date: selectedDate.split('T')[0],
      adult_tickets: adultTickets,
      other_tickets: otherTickets,
      total: adultTickets * adultValue + otherTickets * otherValue,
    });
    history.push(`/ticketSummary?date=${selectedDate}`);
  };

  return {
    isPack,
    loading: (isPack ? loadingPacks : loadingEntrances) || loadingParkSchedule,
    name: staticPack?.name || staticEntrance?.name || '',
    description: staticPack?.description || staticEntrance?.description || '',
    images: staticPack?.packImages || staticEntrance?.entranceImages || [],
    features: staticPack?.features || staticEntrance?.features || [],
    summerSchedule,
    winterSchedule,
    adultTickets,
    otherTickets,
    setAdultTickets,
    setOtherTickets,
    ticketCount,
    adultValue,
    otherValue,
    remaining,
    canAddMoreTickets,
    selectedDate,
    onSelectDate,
    isDateLocked: Boolean(lockedDate),
    unavailableDates,
    soldOutDates,
    onMonthChange,
    timedActivities,
    selectedTimes,
    onSelectTime,
    total: adultTickets * adultValue + otherTickets * otherValue,
    canContinue,
    isRemoval,
    onContinue,
    goBack: () => history.push('/ticketSelection'),
  };
};

export default useTicketInformationViewModel;
